import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { clientsAPI, projectsAPI, usersAPI } from '../utils/api';
import { isAdminRole, getPermLevel } from '../utils/rolesConfig';
import { ConfirmDialog } from '../components/layout/UI';
import AttachmentsPanel from '../components/AttachmentsPanel';
import { useAuth } from '../hooks/useAuth';
import { format } from 'date-fns';

import { useToast, apiError } from '../components/Toast';
import CustomFieldsEditor from '../components/CustomFieldsEditor';
import { useMaster } from '../hooks/useMasters';
import { useEditableRecord, SaveBar } from '../components/UnsavedChanges';
import { ArrowLeft } from 'lucide-react';


const STATUS_COLOR = { Active:'#10b981', Prospect:'#f59e0b', Inactive:'#94a3b8', Archived:'#64748b' };
const PROJ_STATUS_COLOR = { Active:'#10b981','Not Started':'#94a3b8','On Hold':'#f59e0b',Completed:'#3b82f6' };

const INDUSTRIES_FALLBACK = ['Banking & Finance','Technology','Healthcare','Education','Manufacturing',
  'Agriculture','Real Estate','Construction','Retail','NGO / Non-Profit',
  'Government','Consulting','Telecommunications','Energy','Transport & Logistics','Other']
  .map(v => ({ value:v, label:v }));

export default function ClientDetailPage() {
  const toast = useToast();
  const { id }   = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Must come AFTER user is destructured — const is not hoisted, so reading it
  // above threw "Cannot access 'user' before initialization" on every render.
  const isDeveloper = getPermLevel(user?.role) === 'Developer';
  const [client,    setClient]    = useState(null);
  const [error, setError] = useState('');
  const [projects,  setProjects]  = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [users,     setUsers]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  // Form state, dirty tracking, the tab-close warning and the navigation guard
  // all come from one place now, so every converted detail page behaves the
  // same way. See components/UnsavedChanges.jsx.
  const { form, set, discard, dirty, dirtyFields, patch } = useEditableRecord(client);

  // Admin-controlled, same list and same fallback as the Clients page.
  const INDUSTRIES = useMaster('industry', INDUSTRIES_FALLBACK);

  const [saving,    setSaving]    = useState(false);
  const [delOpen,   setDelOpen]   = useState(false);
  const [linkModal, setLinkModal] = useState(false);
  const [linkProj,  setLinkProj]  = useState('');
  const [tab,       setTab]       = useState('projects');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cr, pr, ur] = await Promise.all([
        clientsAPI.getById(id),
        projectsAPI.getAll({ limit: 0 }),
        usersAPI.getAll(),
      ]);
      setClient(cr.data);
      setProjects(cr.data.projects || []);
      setAllProjects((pr.data||[]).filter(p => !p.client_id || p.client_id === id));
      setUsers(ur.data||[]);
      // the hook resets the form whenever the record changes
    } catch(e) { toast.error(apiError(e, 'Failed to save client details.')); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Send only what changed. The API writes only what it is sent, so this
      // also means two people editing different fields do not overwrite
      // each other.
      await clientsAPI.update(id, patch());
      toast.success(`Saved ${dirtyFields.length} change${dirtyFields.length === 1 ? '' : 's'}`);
      load();
    }
    catch(e) { toast.error(apiError(e, 'Could not save those changes.')); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await clientsAPI.delete(id);
      toast.success('Client deleted');
      navigate('/clients');          // only leave the page once it worked
    } catch(e) {
      // 409 when projects are still linked — the message names them.
      toast.error(apiError(e, 'Could not delete this client.'));
      setDelOpen(false);
    }
  };

  const handleLink = async () => {
    if (!linkProj) return;
    await clientsAPI.linkProject(id, { project_id: linkProj });
    setLinkModal(false); load();
  };

  const handleUnlink = async (proj_id) => {
    await clientsAPI.unlinkProject(id, { project_id: proj_id }); load();
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/></div>;
  if (!client) return <div className="card text-center py-16"><p style={{ color:'var(--text)' }}>Client not found</p><button onClick={()=>navigate('/clients')} className="btn-secondary mt-4">← Back</button></div>;

  const clientName = client.type === 'company' ? client.company_name : `${client.first_name||''} ${client.last_name||''}`.trim();
  const initials   = clientName.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
  const sc         = STATUS_COLOR[client.status];

  return (
    <div className="space-y-5">
      {error && (
        <div className="pt-2">
          <div className="p-3 rounded-xl text-sm mb-2" style={{ background:'rgba(239,68,68,.08)', color:'#ef4444', border:'1px solid rgba(239,68,68,.2)' }}>
            ⚠ {error} <button className="ml-2 underline text-xs" onClick={()=>setError('')}>Dismiss</button>
          </div>
        </div>
      )}
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm" style={{ color:'var(--text-secondary)' }}>
        <button onClick={()=>navigate('/clients')} className="flex items-center gap-1.5 hover:text-brand-500 transition-colors">
          <ArrowLeft size={16}/> Clients
        </button>
        <span>/</span>
        <span style={{ color:'var(--text)' }}>{clientName}</span>
      </div>

      {/* Header card */}
      <div className="card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center font-bold text-white text-xl"
              style={{ background: client.type==='company' ? '#2E539F' : '#64748b' }}>
              {initials}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h1 className="text-2xl font-bold" style={{ color:'var(--text)' }}>{clientName}</h1>
                <span className="text-sm px-2.5 py-1 rounded-full font-semibold"
                  style={{ background:sc+'20', color:sc }}>{client.status}</span>
              </div>
              <div className="flex items-center gap-3 text-sm" style={{ color:'var(--text-secondary)' }}>
                <span>{client.type === 'company' ? '🏢 Company' : '👤 Individual'}</span>
                {client.industry && <><span>·</span><span>{client.industry}</span></>}
                {client.country  && <><span>·</span><span>📍 {[client.city, client.country].filter(Boolean).join(', ')}</span></>}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {(client?.created_by === user?.id || isAdminRole(user?.role)) && (
              <button onClick={()=>setDelOpen(true)} className="text-sm px-3 py-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors border" style={{ borderColor:'var(--border)' }}>Delete</button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b" style={{ borderColor:'var(--border)' }}>
        {[{k:'projects',l:`Projects (${projects.length})`},{k:'info',l:'Details'},{k:'documents',l:'Documents'}].map(t=>(
          <button key={t.k} onClick={()=>setTab(t.k)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab===t.k?'border-brand-500 text-brand-500':'border-transparent'}`}
            style={tab!==t.k?{color:'var(--text-secondary)'}:{}}>
            {t.l}
          </button>
        ))}
      </div>

      {/* Projects tab */}
      {tab==='projects' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold" style={{ color:'var(--text)' }}>Linked Projects</h3>
            {/* Linking is an edit, not an administrative act, so it follows the
                same rule as every other edit in the system: open to everyone
                except Developers, which is what the API enforces. */}
            {!isDeveloper && (
              <button onClick={()=>setLinkModal(true)} className="btn-secondary text-sm">+ Link Project</button>
            )}
          </div>
          {projects.length === 0 ? (
            <div className="card text-center py-12">
              <p className="text-3xl mb-2">📁</p>
              <p style={{ color:'var(--text)' }}>No projects linked yet</p>
              <p className="text-sm mt-1" style={{ color:'var(--text-secondary)' }}>Link existing projects or create a new project for this client</p>
            </div>
          ) : (
            <div className="card overflow-hidden p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
                      {['Project Name','Status','Priority','Start Date','End Date','Actions'].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider whitespace-nowrap"
                          style={{ color:'var(--text-secondary)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map(p => (
                      <tr key={p.id} className="border-b last:border-0 hover:bg-[var(--surface-alt)] transition-colors"
                        style={{ borderColor:'var(--border)' }}>
                        <td className="px-4 py-3">
                          <Link to={`/projects/${p.id}`}
                            className="font-semibold hover:text-brand-500 transition-colors"
                            style={{ color:'var(--text)' }}>
                            {p.name}
                          </Link>
                          {p.client && <p className="text-xs mt-0.5" style={{ color:'var(--text-secondary)' }}>{p.client}</p>}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap"
                            style={{ background:(PROJ_STATUS_COLOR[p.status]||'#94a3b8')+'20', color:PROJ_STATUS_COLOR[p.status]||'#94a3b8' }}>
                            {p.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color:'var(--text-secondary)' }}>
                          {p.priority || '—'}
                        </td>
                        <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color:'var(--text-secondary)' }}>
                          {p.start_date ? format(new Date(p.start_date),'MMM d, yyyy') : '—'}
                        </td>
                        <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color:'var(--text-secondary)' }}>
                          {p.end_date ? format(new Date(p.end_date),'MMM d, yyyy') : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Link to={`/projects/${p.id}`}
                              className="text-xs px-2.5 py-1 rounded-lg border hover:bg-[var(--surface-alt)] transition-colors whitespace-nowrap"
                              style={{ borderColor:'var(--border)', color:'var(--text-secondary)' }}>
                              Open →
                            </Link>
                            {!isDeveloper && (
                              <button onClick={() => handleUnlink(p.id)}
                                className="text-xs px-2.5 py-1 rounded-lg border text-red-400 hover:bg-red-50 transition-colors whitespace-nowrap"
                                style={{ borderColor:'#fca5a5' }}>
                                Unlink
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Details tab */}
      {/* Details — the one place a client's fields live. Always editable;
          Save appears below only when something has actually changed. */}
      {tab==='info' && (
        <div className="card space-y-5">

          <div>
            <label className="label">Client type</label>
            <div className="flex rounded-lg border overflow-hidden text-sm w-64" style={{ borderColor:'var(--border)' }}>
              {[{k:'company',l:'Company'},{k:'individual',l:'Individual'}].map(t=>(
                <button key={t.k} onClick={()=>set({ type:t.k,
                  // Clear the other type's identifying fields so a company name
                  // cannot linger on an individual.
                  ...(t.k==='company'
                    ? { first_name:'', last_name:'', national_id:'', date_of_birth:'' }
                    : { company_name:'', registration_number:'', tax_id:'', industry:'', website:'' })})}
                  className={`flex-1 py-2 font-medium transition-colors ${form.type===t.k?'bg-brand-500 text-white':''}`}
                  style={form.type!==t.k?{color:'var(--text-secondary)',background:'var(--surface-alt)'}:{}}>
                  {t.l}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {form.type === 'company' ? (
              <>
                <div className="md:col-span-2"><label className="label">Company name *</label>
                  <input className="input" value={form.company_name||''} onChange={e=>set({company_name:e.target.value})}/></div>
                <div><label className="label">Registration number</label>
                  <input className="input" value={form.registration_number||''} onChange={e=>set({registration_number:e.target.value})}/></div>
                <div><label className="label">Tax ID / TIN</label>
                  <input className="input" value={form.tax_id||''} onChange={e=>set({tax_id:e.target.value})}/></div>
                <div><label className="label">Industry</label>
                  <select className="select" value={form.industry||''} onChange={e=>set({industry:e.target.value})}>
                    <option value="">Not set</option>
                    {INDUSTRIES.map(o=><option key={o.value||o} value={o.value||o}>{o.label||o}</option>)}
                  </select></div>
                <div><label className="label">Website</label>
                  <input className="input" value={form.website||''} onChange={e=>set({website:e.target.value})} placeholder="https://…"/></div>
              </>
            ) : (
              <>
                <div><label className="label">First name *</label>
                  <input className="input" value={form.first_name||''} onChange={e=>set({first_name:e.target.value})}/></div>
                <div><label className="label">Last name *</label>
                  <input className="input" value={form.last_name||''} onChange={e=>set({last_name:e.target.value})}/></div>
                <div><label className="label">National ID</label>
                  <input className="input" value={form.national_id||''} onChange={e=>set({national_id:e.target.value})}/></div>
                <div><label className="label">Date of birth</label>
                  <input type="date" className="input" value={(form.date_of_birth||'').slice(0,10)} onChange={e=>set({date_of_birth:e.target.value})}/></div>
                <div><label className="label">Gender</label>
                  <select className="select" value={form.gender||''} onChange={e=>set({gender:e.target.value})}>
                    <option value="">Not set</option>
                    {['Female','Male','Other','Prefer not to say'].map(g=><option key={g} value={g}>{g}</option>)}
                  </select></div>
              </>
            )}

            <div><label className="label">Contact person</label>
              <input className="input" value={form.contact_person||''} onChange={e=>set({contact_person:e.target.value})}/></div>
            <div><label className="label">Status</label>
              <select className="select" value={form.status||'Active'} onChange={e=>set({status:e.target.value})}>
                {['Active','Inactive','Prospect','Former'].map(v=><option key={v} value={v}>{v}</option>)}
              </select></div>
            <div><label className="label">Email</label>
              <input type="email" className="input" value={form.email||''} onChange={e=>set({email:e.target.value})}/></div>
            <div><label className="label">Phone</label>
              <input className="input" value={form.phone||''} onChange={e=>set({phone:e.target.value})}/></div>
            <div><label className="label">Alternative phone</label>
              <input className="input" value={form.phone_alt||''} onChange={e=>set({phone_alt:e.target.value})}/></div>
            <div><label className="label">Account manager</label>
              <select className="select" value={form.account_manager_id||''} onChange={e=>set({account_manager_id:e.target.value})}>
                <option value="">Unassigned</option>
                {users.filter(u=>u.is_active).map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
              </select></div>
            <div><label className="label">Country</label>
              <input className="input" value={form.country||''} onChange={e=>set({country:e.target.value})}/></div>
            <div><label className="label">City</label>
              <input className="input" value={form.city||''} onChange={e=>set({city:e.target.value})}/></div>
            <div className="md:col-span-2"><label className="label">Address</label>
              <input className="input" value={form.address||''} onChange={e=>set({address:e.target.value})}/></div>
            <div className="md:col-span-2"><label className="label">Notes</label>
              <textarea className="input" rows={3} value={form.notes||''} onChange={e=>set({notes:e.target.value})}/></div>
          </div>

          <CustomFieldsEditor appliesTo="client"
            values={form.custom_fields || {}}
            onChange={v => set({ custom_fields: v })} />

          {/* Not editable here: these are facts about the record, not fields. */}
          <div className="pt-3 border-t grid grid-cols-1 md:grid-cols-2 gap-4" style={{ borderColor:'var(--border)' }}>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider mb-0.5" style={{ color:'var(--text-secondary)' }}>Created by</p>
              <p className="text-sm" style={{ color:'var(--text)' }}>{client.created_by_name || '—'}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider mb-0.5" style={{ color:'var(--text-secondary)' }}>Created</p>
              <p className="text-sm" style={{ color:'var(--text)' }}>
                {client.created_at ? format(new Date(client.created_at),'MMM d, yyyy') : '—'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Documents tab */}
      {tab==='documents' && (
        <div className="card">
          <h3 className="font-semibold text-sm mb-4" style={{ color:'var(--text)' }}>Client Documents</h3>
          <AttachmentsPanel entityType="client" entityId={id} currentUserId={user?.id}/>
        </div>
      )}

      {/* Link project modal */}
      {linkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={()=>setLinkModal(false)}/>
          <div className="relative bg-[var(--surface)] rounded-2xl shadow-2xl w-full max-w-sm z-10 p-6" style={{ border:'1px solid var(--border)' }}>
            <h3 className="font-bold text-base mb-4" style={{ color:'var(--text)' }}>Link Project to {clientName}</h3>
            <label className="label">Select Project</label>
            <select className="select mb-4" value={linkProj} onChange={e=>setLinkProj(e.target.value)}>
              <option value="">Choose a project...</option>
              {allProjects.filter(p=>!projects.some(lp=>lp.id===p.id)).map(p=>(
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <div className="flex gap-2 justify-end">
              <button onClick={()=>setLinkModal(false)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={handleLink} disabled={!linkProj} className="btn-primary text-sm">Link</button>
            </div>
          </div>
        </div>
      )}

      <SaveBar dirtyFields={dirtyFields} discard={discard} onSave={handleSave} saving={saving} />

      <ConfirmDialog isOpen={delOpen} onClose={()=>setDelOpen(false)} onConfirm={handleDelete}
        title="Delete Client" message={`Delete "${clientName}"? All project links will be removed.`}/>
    </div>
  );
}
